import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import CompanyPecBanner from "../components/CompanyPecBanner";
import DataTable from "../components/DataTable";
import LoadingState from "../components/LoadingState";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Truck, Plus } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

export default function SuppliersPage() {
    const { companyId, canAdmin } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ ragione_sociale: "", partita_iva: "", pec_addresses: "", authorized_domains: "", category: "generico" });

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/suppliers");
            setItems(data?.items || []);
        } catch { toast.error("Errore"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId]);

    const save = async (e) => {
        e.preventDefault();
        try {
            await api.post("/suppliers", {
                ragione_sociale: form.ragione_sociale,
                partita_iva: form.partita_iva,
                pec_addresses: form.pec_addresses.split(",").map(s => s.trim()).filter(Boolean),
                authorized_domains: form.authorized_domains.split(",").map(s => s.trim()).filter(Boolean),
                category: form.category,
            });
            toast.success("Fornitore creato");
            setOpen(false); setForm({ ragione_sociale: "", partita_iva: "", pec_addresses: "", authorized_domains: "", category: "generico" });
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    };

    const columns = [
        { key: "ragione_sociale", header: "Fornitore", sortable: true, render: r => (
            <a href={`/suppliers/${r.id}`} data-testid={`supplier-open-${r.id}`} className="flex items-center gap-2 hover:opacity-80">
                <div className="w-8 h-8 rounded-lg bg-[#2563EB]/10 flex items-center justify-center"><Truck className="w-4 h-4 text-[#2563EB]" /></div>
                <div><div className="text-sm font-medium text-[#2563EB] hover:underline">{r.ragione_sociale}</div><div className="text-xs text-[#64748B]">{r.category}</div></div>
            </a>
        )},
        { key: "partita_iva", header: "P.IVA", render: r => <span className="font-mono text-xs">{r.partita_iva}</span> },
        { key: "pec_addresses", header: "Indirizzi PEC", render: r => <span className="text-xs">{(r.pec_addresses||[]).join(", ") || "—"}</span> },
        { key: "authorized_domains", header: "Domini autorizzati", render: r => <span className="text-xs font-mono">{(r.authorized_domains||[]).join(", ") || "—"}</span> },
        { key: "authorized_ibans", header: "IBAN", render: r => (r.authorized_ibans||[]).length },
        { key: "status", header: "Stato", render: r => <span className="text-xs font-semibold text-[#16A34A]">{r.status}</span> },
    ];

    return (
        <div className="space-y-6">
            <PageHeader title="Fornitori" subtitle="Anagrafica dei fornitori riconosciuti." breadcrumbs={[{ label: "Fornitori" }]} actions={
                canAdmin() && <Button onClick={() => setOpen(true)} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white gap-2" data-testid="new-supplier-btn"><Plus className="w-4 h-4" />Nuovo fornitore</Button>
            } />
            <CompanyPecBanner />
            {loading ? <LoadingState /> : <DataTable testId="suppliers-table" columns={columns} data={items} pageSize={15} getRowKey={r => r.id} emptyTitle="Nessun fornitore" />}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-white sm:max-w-lg">
                    <DialogHeader><DialogTitle>Nuovo fornitore</DialogTitle></DialogHeader>
                    <form onSubmit={save} className="space-y-3" data-testid="supplier-form">
                        <div><Label>Ragione sociale</Label><Input data-testid="supplier-name-input" value={form.ragione_sociale} onChange={e => setForm({...form, ragione_sociale: e.target.value})} className="mt-1 h-10" /></div>
                        <div><Label>Partita IVA</Label><Input data-testid="supplier-piva-input" value={form.partita_iva} onChange={e => setForm({...form, partita_iva: e.target.value})} className="mt-1 h-10 font-mono" /></div>
                        <div><Label>Indirizzi PEC (separati da virgola)</Label><Input value={form.pec_addresses} onChange={e => setForm({...form, pec_addresses: e.target.value})} className="mt-1 h-10" /></div>
                        <div><Label>Domini autorizzati (separati da virgola)</Label><Input value={form.authorized_domains} onChange={e => setForm({...form, authorized_domains: e.target.value})} className="mt-1 h-10 font-mono" /></div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
                            <Button type="submit" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="supplier-save-btn">Salva</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
